import { useGetEnrollmentDisclosuresV2Data, useSubmitEnrollmentDisclosuresV2Data } from '@egw/api'
import { Alert, AlertType, Button, GenericError } from '@egw/components'
import { configClient } from '@egw/config'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { EnrollmentV2Layout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useRouter } from '@egw/router'
import { type FC, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { DisclosureSection, PageHeader } from '../../../../components'
import { type Config } from '../../../../config/config'
import { ROUTES } from '../../../../constants/routes/routes-generic'
import { useValidateRoute } from '../../../../hooks/navigation/validate-route'
import { getArticlePath } from '../../../../utils/disclosures/get-disclosures-path'
import { getInvestmentsDisclosures } from '../../../../utils/disclosures/get-multi-disclosures'
import { newPathIsDifferent } from '../../../../utils/general/new-path-is-different'

const log = new Logger('Investments - Disclosure')

export const DisclosureWrapperPage: FC<{ accountType?: 'brokerage' | 'traditional' }> = ({ accountType }) => {
  return <DisclosuresV2Page accountType={accountType} />
}

export const DisclosuresV2Page: FC<{ accountType?: 'brokerage' | 'traditional' }> = ({ accountType }) => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()
  const config = configClient.getConfig() as Config
  const { flags } = useFlags<FeatureFlags>()
  const { data, isLoading, error } = useGetEnrollmentDisclosuresV2Data()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentDisclosuresV2Data()

  const disclosures = useMemo(() => {
    return getInvestmentsDisclosures(
      config.zendeskArticles,
      data?.enrollmentData?.userInfo?.investment?.disclosures ?? [],
      accountType,
    )
  }, [config.zendeskArticles, data?.enrollmentData?.userInfo?.investment?.disclosures, accountType])

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      egwa: false,
      csa: false,
    },
  })

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (): Promise<void> => {
    if (isValid) {
      const apexDisclosuresPayload =
        data?.enrollmentData?.userInfo?.investment?.disclosures?.map((e) => e.disclosureId) ?? []

      const payload = {
        // Cash Account Disclosures
        depositAgreementDisclosure: {
          id: getArticlePath(config.zendeskArticles.depositAgreement),
          accepted: true,
        },
        taxCertificationForUSPersonsDisclosure: {
          id: getArticlePath(config.zendeskArticles.taxCertification),
          accepted: true,
        },
        privacyNotice: {
          id: getArticlePath(config.zendeskArticles.privacyPolicySection),
          accepted: true,
        },
        // End Cash Account Disclosures
        investmentAdvisoryAgreementDisclosure: {
          id: getArticlePath(config.zendeskArticles.investmentAdvisoryAgreement),
          accepted: true,
        },
        advWrapFeeDisclosure: {
          id: getArticlePath(config.zendeskArticles.advPart2A2b),
          accepted: true,
        },
        advisoryPrivacyNotice: {
          id: getArticlePath(config.zendeskArticles.privacyPolicyAdvisors),
          accepted: true,
        },
        customerRelationshipSummaryDisclosure: {
          id: getArticlePath(config.zendeskArticles.customerRelationshipAgreement),
          accepted: true,
        },
        investmentProfile: {
          acceptedDisclosureIds: apexDisclosuresPayload,
        },
      }

      try {
        const response = await mutate(payload)
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  const closeHandler = () => {
    try {
      mutate({
        abandonApplication: true,
      })
      navigate(WEALTH_ROUTES.INVESTMENTS.path)
    } catch (e) {
      log.error('Error abandoning application', e)
    }
  }

  // Determine which title to use based on feature flag and account type
  const title = useMemo(() => {
    return flags.rolloutJointEnrollmentV1
      ? t('investments_v2.disclosures.title_joint_enrollment')
      : t('investments_v2.disclosures.title')
  }, [flags.rolloutJointEnrollmentV1, t])

  // Determine which continue text to use based on feature flag and account type
  const continueText = useMemo(() => {
    return t('investments_v2.disclosures.continue')
  }, [t])

  return (
    <EnrollmentV2Layout closeHandler={closeHandler} title={title}>
      <div className="h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <form onSubmit={(e) => void onSubmit(e)} className="flex-1 flex flex-col justify-start">
            <PageHeader title={title} />
            <p className="typo-primary-base text-text-meta mb-md">{t('investments_v2.disclosures.description_1')}</p>
            {disclosures.map((e, i) => {
              return (
                <Controller
                  name={e.name as 'egwa' | 'csa'}
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <DisclosureSection
                      checkboxHelperText={e.helper}
                      checkboxLabel={e.title}
                      expandedLinks={e.links}
                      expandedText={e.expanded}
                      hasBorder={disclosures.length !== i + 1}
                      testId={`disclosure-${e.title.toLowerCase().replaceAll(' ', '-')}`}
                      onCheckboxChange={field.onChange}
                    />
                  )}
                  key={`disclosure-${e.title.replaceAll(' ', '-')}`}
                />
              )
            })}

            <div className="flex-1 md:hidden" />

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-lg"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="enrollment-continue-button"
            >
              {continueText}
            </Button>
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentV2Layout>
  )
}
